import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { Box, Typography } from '@mui/material';
import { ThemeContext } from '@gv/triage-components';
import { StandardButton } from '@gv/triage-components';
import { useState, useEffect, useContext } from 'react';
import { getFormattedAge } from '@gv/triage-components/dist/utils/date';

import { formatDateTime } from 'utils/config';
import { useListQuery as usebitwerxPetDetailAPIQuery } from 'store/api/bitwerx-pet-detail';

import { Value, Heading } from './pims-styles';
export const PimsInfo = (client: any) => {
  const [selectedPetId, setSelectedPetId] = useState<number | null>(null);
  const [petName, setPetName] = useState<string | null>('');
  useEffect(() => {
    const filteredPets = (client?.client?.pets || []).filter((pet: any) => {
      return (
        pet.bitwerx_patient_id &&
        pet.bitwerx_patient_id !== null &&
        pet.bitwerx_client_id !== null
      );
    });
    if (filteredPets.length > 0) {
      setPetName(filteredPets[0].name);
      setSelectedPetId(filteredPets[0].bitwerx_patient_id);
    } else {
      setSelectedPetId(null);
      setPetName('');
    }
  }, [client?.client?.pets]);

  const { data: data }: any = usebitwerxPetDetailAPIQuery({
    id: selectedPetId,
    hospitalId: client?.client?.hospital_id,
    clientId: client?.client?.bitwerx_client_id,
  });
  const { isDarkTheme } = useContext(ThemeContext);

  // Determine if pets list is long enough to require scrolling
  const pets =
    client?.client?.pets?.filter(
      (pet: any) => pet.bitwerx_patient_id && pet.bitwerx_patient_id !== null
    ) || [];
  const petsScrollable = pets.length > 1;

  return (
    <Box
      sx={{
        width: '100%',
        height: {
          xs: 'auto',
          md: '850px',
          lg: !petsScrollable ? '600px' : '700px',
        },
      }}
    >
      <Grid container spacing={2}>
        <Grid item md={8} xs={12} style={{ height: '100%' }}>
          <Box sx={{ height: '100%', overflowY: 'visible' }}>
            <Paper
              elevation={1}
              sx={{
                padding: 2,
                width: '100%',
                boxShadow: 'none',
                borderRadius: '20px !important',
                backgroundColor: isDarkTheme ? 'rgb(41,41,41)' : '#F3F6FC',
                border: isDarkTheme
                  ? '1px solid rgb(41,41,41)'
                  : '1px solid #F3F6FC',
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 2, pl: 1, color: isDarkTheme ? '#ffffff' : '' }}
              >
                Client Information
              </Typography>

              <Paper
                elevation={1}
                sx={{
                  padding: 2,
                  width: '100%',
                  boxShadow: 'none',
                  border: '1px #e6e6e6',
                  borderRadius: '20px !important',
                  backgroundColor: isDarkTheme ? '#3C3C3C' : '#ffffff',
                }}
              >
                <Grid
                  container
                  spacing={2}
                  sx={{ color: isDarkTheme ? '#ffffff' : '' }}
                >
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading>First Name</Heading>
                    <Value>{client?.client?.first_name ?? '--'}</Value>
                  </Grid>
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Last Name</Heading>
                    <Value>{client?.client?.last_name ?? '--'}</Value>
                  </Grid>
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Phone</Heading>
                    <Value>{client?.client?.phone ?? '--'}</Value>
                  </Grid>

                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Address</Heading>
                    <Value>{client?.client?.address ?? '--'}</Value>
                  </Grid>
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Email</Heading>
                    <Value>{client?.client?.email ?? '--'}</Value>
                  </Grid>
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Upcoming Appointments</Heading>
                    <Value>
                      {data?.clientAppointment?.startTime &&
                      new Date(data?.clientAppointment?.startTime) > new Date()
                        ? formatDateTime(data?.clientAppointment?.startTime)
                        : '--'}
                    </Value>
                  </Grid>
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Alerts/Flags</Heading>
                    <Value sx={{ color: 'red' }}>
                      {data?.clientAlerts?.message ?? '--'}
                    </Value>
                  </Grid>

                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Special Notes</Heading>
                    <Value>{data?.clientNotes?.value ?? '--'}</Value>
                  </Grid>
                </Grid>
              </Paper>
            </Paper>
            <Paper
              elevation={1}
              sx={{
                mt: 2,
                padding: 2,
                width: '100%',
                boxShadow: 'none',
                borderRadius: '20px !important',
                backgroundColor: isDarkTheme ? 'rgb(41,41,41)' : '#F3F6FC',
                border: isDarkTheme
                  ? '1px solid rgb(41,41,41)'
                  : '1px solid #F3F6FC',
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 2, pl: 1, color: isDarkTheme ? '#ffffff' : '' }}
              >
                Pets Information
              </Typography>
              <Box
                sx={{
                  pr: petsScrollable ? 1 : 0,
                  overflowY: petsScrollable
                    ? { xs: 'auto', sm: 'auto', md: 'auto', lg: 'auto' }
                    : 'visible',
                  maxHeight: petsScrollable
                    ? {
                        xs: 650,
                        sm: 650,
                        md: 380,
                        lg: 300,
                      }
                    : 'none',
                }}
              >
                {pets?.map((pet: any, index: any) => (
                  <Paper
                    elevation={1}
                    key={pet.bitwerx_patient_id || index}
                    sx={{
                      padding: 2,
                      width: '100%',
                      boxShadow: 'none',
                      border: '1px #e6e6e6',
                      marginTop: index === 0 ? 0 : 2,
                      borderRadius: '20px !important',
                      backgroundColor: isDarkTheme ? '#3C3C3C' : '#ffffff',
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                      sx={{ color: isDarkTheme ? '#ffffff' : '' }}
                    >
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading>Pet Name</Heading>
                        <Value>{pet?.name ?? '--'}</Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Species</Heading>
                        <Value>{pet?.species ?? '--'}</Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Breed</Heading>
                        <Value>{pet?.breed ?? '--'}</Value>
                      </Grid>

                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Gender</Heading>
                        <Value>{pet?.gender ?? '--'}</Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Age</Heading>
                        <Value>
                          {pet?.date_of_birth
                            ? getFormattedAge(pet?.date_of_birth)
                            : '--'}
                        </Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Last weight taken</Heading>
                        <Value>{data?.petDetails?.vital?.value ?? '--'}</Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Last Visit Date</Heading>
                        <Value>
                          {data?.petDetails?.visits?.checkIn
                            ? formatDateTime(data?.petDetails?.visits?.checkIn)
                            : '--'}
                        </Value>
                      </Grid>

                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Appearance</Heading>
                        <Value>{data?.petDetails?.appearance ?? '--'}</Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Deceased</Heading>
                        <Value>
                          {data?.petDetails?.isDeceased
                            ? data?.petDetails?.isDeceased === true
                              ? 'Yes'
                              : 'No'
                            : '--'}
                        </Value>
                      </Grid>
                    </Grid>
                    {pets.length > 1 && (
                      <Grid item md={12} xs={12}>
                        <StandardButton
                          text="View Other Detail"
                          style={{
                            marginTop: '10px',
                          }}
                          onClick={() => {
                            setSelectedPetId(pet.bitwerx_patient_id);
                            setPetName(pet.name);
                          }}
                        />
                      </Grid>
                    )}
                  </Paper>
                ))}
              </Box>
            </Paper>
          </Box>
        </Grid>
        <Grid
          item
          md={4}
          xs={12}
          sx={{
            height: {
              xs: 'auto',
              md: !petsScrollable ? '765px' : '850px',
              lg: !petsScrollable ? '600px' : '705px',
              xl: !petsScrollable ? '580px' : '687px',
            },
          }}
        >
          <Paper
            elevation={1}
            sx={{
              padding: 2,
              width: '100%',
              height: '100%',
              boxShadow: 'none',
              borderRadius: '20px !important',
              backgroundColor: isDarkTheme ? 'rgb(41,41,41)' : '#F3F6FC',
              border: isDarkTheme
                ? '1px solid rgb(41,41,41)'
                : '1px solid #F3F6FC',
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mb: 2, pl: 1, color: isDarkTheme ? '#ffffff' : '' }}
            >
              {petName} Details
            </Typography>
            <Box
              sx={{
                height: '100%',
                maxHeight: '100%',
                overflowY: 'auto',
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  padding: 2,
                  width: '100%',
                  display: 'flex',
                  boxShadow: 'none',
                  border: '1px #e6e6e6',
                  flexDirection: 'column',
                  borderRadius: '20px !important',
                  backgroundColor: isDarkTheme ? '#3C3C3C' : '#ffffff',
                  height: { lg: '92%', md: '89%', sm: 'auto', xs: 'auto' },
                }}
              >
                <Grid
                  container
                  spacing={2}
                  sx={{ color: isDarkTheme ? '#ffffff' : '' }}
                >
                  <Grid item xl={6} lg={12} md={12} sm={12}>
                    <Heading>Last Appointment</Heading>
                    <Value>
                      {data?.petDetails?.appointment?.startTime
                        ? formatDateTime(
                            data?.petDetails?.appointment?.startTime
                          )
                        : '--'}
                    </Value>
                  </Grid>
                  <Grid item xl={6} lg={12} md={12} sm={12}>
                    <Heading fontWeight="bold">Last Prescription Date</Heading>
                    <Value>
                      {data?.petDetails?.prescriptions?.issuedDate
                        ? formatDateTime(
                            data?.petDetails?.prescriptions?.issuedDate
                          )
                        : '--'}
                    </Value>
                  </Grid>
                  <Grid item xl={6} lg={12} md={12} sm={12}>
                    <Heading fontWeight="bold">Reason for Last Visit</Heading>
                    <Value>{data?.petDetails?.visits?.reason ?? '--'}</Value>
                  </Grid>

                  <Grid item xl={6} lg={12} md={12} sm={12}>
                    <Heading fontWeight="bold">Prescriptions</Heading>
                    <Value>
                      {data?.petDetails?.prescriptions?.description ?? '--'}
                    </Value>
                  </Grid>
                  <Grid item xl={6} lg={12} md={12} sm={12}>
                    <Heading fontWeight="bold">Medical Diagnoses</Heading>
                    <Value>
                      {data?.petDetails?.diagnosis?.description ?? '--'}
                    </Value>
                  </Grid>
                  {data?.petDetails?.vaccinations?.map(
                    (value: any, index: any) => {
                      if (index < 10) {
                        return (
                          <Grid item xl={6} lg={12} md={12} sm={12}>
                            <Heading fontWeight="bold">
                              {value?.vaccinationType}
                            </Heading>
                            <Value>
                              {value?.issuedDate
                                ? formatDateTime(value?.issuedDate)
                                : '--'}
                            </Value>
                          </Grid>
                        );
                      }
                    }
                  )}
                  {data?.petDetails?.labResult?.map(
                    (value: any, index: any) => {
                      if (index < 10) {
                        return (
                          <Grid item xl={6} lg={12} md={12} sm={12}>
                            <Heading fontWeight="bold">{value?.name}</Heading>
                            <Value>{value?.result ?? '--'}</Value>
                          </Grid>
                        );
                      }
                    }
                  )}
                </Grid>
              </Paper>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
